import {
  SupplierRelationshipFulfillmentType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { CountryCodeDictionary, formatCurrency } from "@sellar-io/common";
import { Badge, Button } from "@sellar-io/components";
import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from "react";
import { Icon } from "../../../components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { useBasketContext } from "../../../hooks";
import { DeliveryMethodRadioSelector } from "./DeliveryMethodRadioSelector";
import { getDeliveryMethodInitialValue } from "./DeliveryMethodRadioSelector.shared";
import { StorefrontDeliveryOptionsDialog } from "../categoryPage/Storefront/StorefrontDialogs/StorefrontDeliveryOptionsDialog";
import { OrderInformationButton } from "../checkoutV2/lib/Checkout";
import {
  CheckoutSelectedFulfillmentType,
  useStockListOrderSummaryProps,
} from "..";
import { useStorefrontBuyerPostcode } from "../categoryPage/Storefront/lib/useStorefrontBuyerPostcode";

type CheckoutFulfillmentTypeSelectorProps = {
  supplierRelationshipFulfillmentType:
    | SupplierRelationshipFulfillmentType
    | undefined;
  value: CheckoutSelectedFulfillmentType;
  supplier: SupplierGetResponseType;
  order: ReturnType<typeof useBasketContext>["0"]["order"];
  supplierStorefront: SupplierStorefrontV2GetResponseType;
  onChange: (data: {
    fulfillmentType: CheckoutSelectedFulfillmentType;
  }) => Promise<void> | void;
  renderFooter?: (params: {
    eligibleFulfillmentTypes: CheckoutSelectedFulfillmentType[];
  }) => ReactNode;
};

export const CheckoutFulfillmentTypeSelector = ({
  supplierRelationshipFulfillmentType,
  value,
  supplier,
  order,
  supplierStorefront,
  onChange,
  renderFooter,
}: CheckoutFulfillmentTypeSelectorProps) => {
  const [selectorActive, setSelectorActive] = useState(false);
  const [moreInfoDialogActive, setMoreInfoDialogActive] = useState(false);

  const initialValue = useMemo(
    () =>
      value ??
      getDeliveryMethodInitialValue({
        connection: {
          fulfillmentType: supplierRelationshipFulfillmentType ?? null,
        },
        supplierStorefront,
        supplier,
      }),
    [value, supplierRelationshipFulfillmentType, supplierStorefront, supplier],
  );

  const [updatedValue, setUpdatedValue] =
    useState<CheckoutSelectedFulfillmentType>(initialValue);
  const [previousInitialValue, setPreviousInitialValue] =
    useState(initialValue);
  if (initialValue !== previousInitialValue) {
    setPreviousInitialValue(initialValue);
    setUpdatedValue(initialValue);
  }

  const collectionAddress =
    supplier.CollectionFromAddress || supplier.Business.DeliveryAddress;

  const directDeliveryData = useGetDeliveryData({
    supplierStorefront,
    fulfillmentType: "direct",
    order,
  });

  const haulierDeliveryData = useGetDeliveryData({
    supplierStorefront,
    fulfillmentType: "haulier",
    order,
  });

  const courierDeliveryData = useGetDeliveryData({
    supplierStorefront,
    fulfillmentType: "courier",
    order,
  });

  const details = (() => {
    switch (initialValue) {
      case "sellarDistribution":
        return {
          title: "Delivered by Sellar",
          subtitle: "Sellar delivery via your scheduled route",
          price: "FREE",
        };
      case "direct":
        return {
          title: `Delivered by ${supplier.Business.name}`,
          subtitle: "Direct supplier delivery",
          price: `${formatCurrency(directDeliveryData.deliveryCharge)}${directDeliveryData.isDeliveryChargeCapped ? "" : " (ex. VAT)"}`,
        };
      case "courier":
        return {
          title: "Courier",
          subtitle: "Courier delivery",
          price: `${formatCurrency(courierDeliveryData.deliveryCharge)}${courierDeliveryData.isDeliveryChargeCapped ? "" : " (ex. VAT)"}`,
        };
      case "haulier":
        return {
          title: "Pallet",
          subtitle: "Pallet delivery",
          price: `${formatCurrency(haulierDeliveryData.deliveryCharge)}${haulierDeliveryData.isDeliveryChargeCapped ? "" : " (ex. VAT)"}`,
        };
      case "collection":
      default:
        return {
          title: "Collection from supplier",
          subtitle: `Arrange a collection with ${supplier.Business.name} at ${collectionAddress.city}, ${collectionAddress.postalCode}`,
          price: "FREE",
        };
    }
  })();

  if (supplier.onlyAllowCardPaymentWithDeposit) {
    return (
      <>
        <div className="flex flex-col gap-2 rounded-lg bg-white p-4">
          <Badge icon={"FERRY"}>Sellar imports</Badge>
          <p>
            Your order will be shipped from {supplier.Business.name} in{" "}
            {supplier.Business.DeliveryAddress.city},{" "}
            {
              CountryCodeDictionary[
                supplier.Business.DeliveryAddress
                  .country as keyof typeof CountryCodeDictionary
              ]
            }{" "}
            to our warehouse.
          </p>
          <div
            onClick={() => setMoreInfoDialogActive(true)}
            className={
              "m-0 flex cursor-pointer flex-row items-center gap-2 p-0 text-blue-800"
            }
          >
            <Icon icon={"OPEN_IN_NEW"} />
            <p className={"font-semibold"}>MORE INFO</p>
          </div>
        </div>

        <StorefrontDeliveryOptionsDialog
          active={moreInfoDialogActive}
          onDismiss={() => {
            setMoreInfoDialogActive(false);
          }}
          supplier={supplier}
          connection={{
            fulfillmentType: supplierRelationshipFulfillmentType ?? null,
          }}
          unAuthorisedUserPostcode={null}
          fulfillmentType={initialValue}
          order={{ ...order, RetailerId: 1 }}
        />
      </>
    );
  }

  return (
    <>
      <OrderInformationButton
        startIcon="TRUCK_OUTLINE"
        endIcon="PENCIL"
        endText={<b>{details.price}</b>}
        title={details.title}
        subtitle={details.subtitle}
        subtitleFallback=""
        onClick={() => setSelectorActive(true)}
        dialog={
          <CheckoutFulfillmentTypeSelectorDialog
            selectorActive={selectorActive}
            fulfillmentType={updatedValue}
            order={order}
            supplier={supplier}
            supplierStorefront={supplierStorefront}
            supplierRelationshipFulfillmentType={
              supplierRelationshipFulfillmentType
            }
            setFulfillmentType={setUpdatedValue}
            setSelectorActive={setSelectorActive}
            onChange={onChange}
          />
        }
      />
      {renderFooter?.({
        eligibleFulfillmentTypes: [
          ...new Set(
            supplierStorefront.deliveryMethods
              ?.map((deliveryMethod) => deliveryMethod.fulfillmentType)
              .filter(Boolean) ?? [],
          ),
        ],
      })}
    </>
  );
};

const CheckoutFulfillmentTypeSelectorDialog = ({
  selectorActive,
  fulfillmentType,
  order,
  supplier,
  supplierStorefront,
  supplierRelationshipFulfillmentType,
  setSelectorActive,
  setFulfillmentType,
  onChange,
}: {
  selectorActive: boolean;
  fulfillmentType: CheckoutSelectedFulfillmentType;
  order: CheckoutFulfillmentTypeSelectorProps["order"];
  supplier: CheckoutFulfillmentTypeSelectorProps["supplier"];
  supplierStorefront: CheckoutFulfillmentTypeSelectorProps["supplierStorefront"];
  supplierRelationshipFulfillmentType: CheckoutFulfillmentTypeSelectorProps["supplierRelationshipFulfillmentType"];
  setSelectorActive: Dispatch<SetStateAction<boolean>>;
  setFulfillmentType: Dispatch<SetStateAction<CheckoutSelectedFulfillmentType>>;
  onChange: CheckoutFulfillmentTypeSelectorProps["onChange"];
}) => {
  const { buyerPostcode } = useStorefrontBuyerPostcode();
  return (
    <Dialog active={selectorActive} onDismiss={() => setSelectorActive(false)}>
      <DialogNavigation
        title="Select delivery method"
        onClickLeftIcon={() => setSelectorActive(false)}
        leftIcon="CHEVRON_DOWN"
      />
      <DialogContent>
        <DeliveryMethodRadioSelector
          value={fulfillmentType}
          onUpdate={(selectedValue) => setFulfillmentType(selectedValue)}
          onConfirmIneligibleUpdate={(selectedValue) => {
            setFulfillmentType(selectedValue);
            void onChange({ fulfillmentType: selectedValue });
            setSelectorActive(false);
          }}
          connection={{
            fulfillmentType: supplierRelationshipFulfillmentType ?? null,
          }}
          order={order}
          supplier={supplier}
          supplierStorefront={supplierStorefront}
          buyerPostcode={buyerPostcode}
        />
      </DialogContent>
      <DialogCTA>
        <Button
          onClick={async () => {
            await onChange({ fulfillmentType });
            setSelectorActive(false);
          }}
        >
          Confirm
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

const useGetDeliveryData = ({
  supplierStorefront,
  fulfillmentType,
  order,
}: {
  supplierStorefront: SupplierStorefrontV2GetResponseType | null;
  fulfillmentType: CheckoutSelectedFulfillmentType;
  order: ReturnType<typeof useBasketContext>["0"]["order"];
}) => {
  const deliveryMethod =
    supplierStorefront?.deliveryMethods?.find(
      (deliveryMethod) => deliveryMethod.fulfillmentType === fulfillmentType,
    ) ?? null;

  const deliveryChargeInput = deliveryMethod?.charge;

  const deliveryChargeCap = deliveryMethod?.deliveryChargeCap;

  const fulfillmentTypeContainerDeliveryCharges =
    deliveryMethod?.FulfillmentTypeContainerDeliveryCharges ??
    supplierStorefront?.FulfillmentType.FulfillmentTypeContainerDeliveryCharges;

  const priceTierDiscounts =
    supplierStorefront?.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const priceTierOrderRules =
    deliveryMethod?.OrderRules?.PriceTierOrderRules ??
    supplierStorefront?.OrderRules?.PriceTierOrderRules ??
    [];

  const minimumOrderPrice =
    deliveryMethod?.OrderRules?.minimumOrderPrice ??
    supplierStorefront?.OrderRules?.minimumOrderPrice ??
    0;

  const freeDeliveryBasketSubtotal =
    deliveryMethod?.freeDeliveryBasketSubtotal ?? null;

  const containerTypeDeliveryChargeRules =
    deliveryMethod?.ContainerTypeDeliveryChargeRules ??
    supplierStorefront?.FulfillmentType.ContainerTypeDeliveryChargeRules ??
    [];

  const {
    apiValues: { deliveryCharge, isDeliveryChargeCapped },
    deliveryChargeWithoutRules,
  } = useStockListOrderSummaryProps({
    order,
    deliveryChargeRules: [],
    deliveryCharge: deliveryChargeInput,
    deliveryChargeCap,
    fulfillmentTypeContainerDeliveryCharges,
    priceTierDiscounts,
    caseSizeRuleList: supplierStorefront?.CaseSizeRules.list ?? [],
    priceTierOrderRules,
    minimumOrderPrice,
    containerTypeDeliveryChargeRules,
    freeDeliveryBasketSubtotal,
  });

  return {
    deliveryCharge,
    isDeliveryChargeCapped,
    deliveryChargeWithoutRules,
  };
};
